import { useState, useRef, useCallback, useEffect } from "react";
import { SOUNDS, SoundId } from "./useDrumSynth";

export type Pattern = Record<SoundId, boolean[]>;

function createEmptyPattern(): Pattern {
  const pattern: Partial<Pattern> = {};
  SOUNDS.forEach((s) => {
    pattern[s.id] = new Array(16).fill(false);
  });
  return pattern as Pattern;
}

export function useSequencer(onTrigger: (sound: SoundId) => void) {
  const [pattern, setPattern] = useState<Pattern>(createEmptyPattern);
  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [bpm, setBpm] = useState(120);

  const intervalRef = useRef<number | null>(null);
  const patternRef = useRef(pattern);

  // Keep patternRef in sync
  useEffect(() => {
    patternRef.current = pattern;
  }, [pattern]);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setPlaying(false);
    setCurrentStep(0);
  }, []);

  const play = useCallback(() => {
    if (playing) {
      stop();
      return;
    }

    setPlaying(true);
    let step = 0;

    const stepDuration = (60 / bpm / 4) * 1000; // 16th notes

    const tick = () => {
      setCurrentStep(step);

      // Trigger sounds for this step
      SOUNDS.forEach((sound) => {
        if (patternRef.current[sound.id][step]) {
          onTrigger(sound.id);
        }
      });

      step = (step + 1) % 16;
    };

    // Play first step immediately
    tick();

    intervalRef.current = window.setInterval(tick, stepDuration);
  }, [playing, bpm, onTrigger, stop]);

  const toggleStep = useCallback((sound: SoundId, step: number) => {
    setPattern((prev) => {
      const newPattern = { ...prev };
      newPattern[sound] = [...prev[sound]];
      newPattern[sound][step] = !prev[sound][step];
      return newPattern;
    });
  }, []);

  const clearPattern = useCallback(() => {
    setPattern(createEmptyPattern());
  }, []);

  const updateBpm = useCallback(
    (newBpm: number) => {
      const clampedBpm = Math.max(60, Math.min(200, newBpm));
      setBpm(clampedBpm);

      // Restart if playing to apply new tempo
      if (playing && intervalRef.current) {
        clearInterval(intervalRef.current);
        const stepDuration = (60 / clampedBpm / 4) * 1000;

        let step = currentStep;
        intervalRef.current = window.setInterval(() => {
          step = (step + 1) % 16;
          setCurrentStep(step);

          SOUNDS.forEach((sound) => {
            if (patternRef.current[sound.id][step]) {
              onTrigger(sound.id);
            }
          });
        }, stepDuration);
      }
    },
    [playing, currentStep, onTrigger]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    pattern,
    playing,
    currentStep,
    bpm,
    play,
    stop,
    toggleStep,
    clearPattern,
    updateBpm,
  };
}
